
import { post } from "@/services/apiService";

interface PasswordResetRequestResponse {
  success: boolean;
  message: string;
}

interface PasswordResetResponse {
  success: boolean;
  message: string;
}

interface PasswordChangeResponse {
  success: boolean;
  message: string;
}

/**
 * Sends a password reset request to the API
 * @param email The user's email address
 * @returns A promise that resolves to a PasswordResetRequestResponse
 */
export const requestPasswordReset = async (email: string): Promise<PasswordResetRequestResponse> => {
  const API_URL = import.meta.env.VITE_APP_CONGNESS_API_URL as string;

  try {
    const response = await fetch(`${API_URL}/forgot_password/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/ld+json' },
      body: JSON.stringify({ email }),
    });

    if (response.ok) {
      return { success: true, message: "Jelszó visszaállítási email elküldve." };
    }

    let detail = '';
    try {
      const body = await response.json();
      detail = body?.detail ?? body?.['hydra:description'] ?? body?.message ?? '';
    } catch {
      // body not JSON — leave detail empty
    }

    if (detail.includes('User is not yet verified')) {
      return { success: false, message: "Ez a felhasználó nem aktív." };
    }

    return {
      success: false,
      message: "Nem sikerült elküldeni a jelszó visszaállítási emailt.",
    };
  } catch (error) {
    console.error('Error requesting password reset:', error);
    return {
      success: false,
      message: "Nem sikerült elküldeni a jelszó visszaállítási emailt."
    };
  }
};

/**
 * Resets a user's password using a valid reset token
 * @param token The password reset token
 * @param newPassword The new password
 * @returns A promise that resolves to a PasswordResetResponse
 */
export const resetPassword = async (token: string, newPassword: string): Promise<PasswordResetResponse> => {
  try {
    const response = await post<PasswordResetResponse>('/forgot_password/' + token, {
      password: newPassword
    });

    return {
      success: true,
      message: "Jelszó sikeresen frissítve."
    };
  } catch (error) {
    console.error('Error resetting password:', error);
    return {
      success: false,
      message: "Nem sikerült frissíteni a jelszót. A token érvénytelen vagy lejárt."
    };
  }
};

/**
 * Changes a user's password (for logged-in users)
 * @param oldPassword The user's current password
 * @param newPassword The new password
 * @returns A promise that resolves to a PasswordChangeResponse
 */
export const changePassword = async (oldPassword: string, newPassword: string): Promise<PasswordChangeResponse> => {
  try {
    const response = await post<PasswordChangeResponse>('/change_password', {
      oldPassword,
      newPassword
    });

    return {
      success: true,
      message: "Jelszó sikeresen frissítve."
    };
  } catch (error) {
    console.error('Error changing password:', error);
    return {
      success: false,
      message: "Nem sikerült frissíteni a jelszót. A jelenlegi jelszó nem megfelelő."
    };
  }
};
